import { useI18n } from "@/contexts/I18nContext";
import { useAuth } from "@/contexts/AuthContext";
import { useToast } from "@/contexts/ToastContext";
import { LOCALES, type Locale } from "@/i18n";

/**
 * English, German or French — one dropdown in the top bar.
 *
 * A row of buttons until 2026-10-05: with three languages it crowded the top
 * bar and read as part of the navigation (founder, after the first French
 * lesson). A native `<select>` is one control, works with the keyboard and
 * screen readers without help, and opens the platform's own picker on a
 * tablet.
 *
 * The names are written in their own language — `Deutsch`, not `German` —
 * which is the one place in this app where an untranslated string is correct.
 *
 * **A switch is saved to the account, not just to this browser.** The choice
 * has to outlive a reload, and the profile is the only place it can: it is also
 * what decides the language of the emails the backend sends and of the
 * credential slips it prints, so a teacher who chooses German here gets German
 * everywhere rather than German in one window.
 */

const NATIVE_NAMES: Record<Locale, string> = { en: "English", de: "Deutsch", fr: "Français" };

export function LanguageSwitcher() {
  const { locale, setLocale, t } = useI18n();
  const { user, updateLocale } = useAuth();
  const { showError } = useToast();

  const choose = (option: Locale) => {
    setLocale(option);
    // The switch has already happened on screen; saving it is a round trip
    // that nobody should watch. A user still holding a printed password cannot
    // call the backend at all, so their choice stays local until they have
    // replaced it — a few seconds later.
    if (user && !user.mustChangePassword) {
      updateLocale(option).catch(showError);
    }
  };

  return (
    <label className="relative inline-flex shrink-0 items-center">
      <span className="sr-only">{t("common.appLanguage")}</span>
      <svg
        aria-hidden="true"
        viewBox="0 0 16 16"
        className="pointer-events-none absolute left-2.5 h-3.5 w-3.5 text-ink-muted"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.4"
      >
        <circle cx="8" cy="8" r="6.3" />
        <path d="M1.7 8h12.6M8 1.7c1.8 1.7 2.7 3.8 2.7 6.3S9.8 12.6 8 14.3M8 1.7C6.2 3.4 5.3 5.5 5.3 8s.9 4.6 2.7 6.3" />
      </svg>
      <select
        data-testid="language-select"
        value={locale}
        lang={locale}
        onChange={(event) => choose(event.target.value as Locale)}
        className="h-9 cursor-pointer appearance-none rounded-control border border-line bg-surface pl-8 pr-7 text-[13px] text-ink transition-colors hover:border-line-strong focus:outline-none focus-visible:ring-2 focus-visible:ring-brand focus-visible:ring-offset-2"
      >
        {LOCALES.map((option) => (
          <option key={option} value={option} lang={option}>
            {NATIVE_NAMES[option]}
          </option>
        ))}
      </select>
      <svg
        aria-hidden="true"
        viewBox="0 0 12 12"
        className="pointer-events-none absolute right-2.5 h-3 w-3 text-ink-muted"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.5"
      >
        <path d="M3 4.5 6 7.5 9 4.5" />
      </svg>
    </label>
  );
}
